import { useEffect, useRef, useState } from "react";
import { useT } from "../../i18n/I18nProvider";
import { TabButton } from "../TabButton";
import type { TabKey } from "../tabs";
import { personalSectionId, type NavItem } from "./nav";

/** How long a jump may scroll before the list starts following the page again. */
const JUMP_MS = 900;

/**
 * The list of the person's own screens beside the portal's account page.
 *
 * The page is ONE long scroll (see `PersonalPage`), so this list does two
 * jobs: a click jumps to a screen, and plain scrolling moves the mark to the
 * screen that is under the top of the window. From `lg` up it is a sticky
 * column on the left; on a phone it is a sticky row of chips under the header
 * that slides by itself to keep the marked chip in sight.
 */
export function PersonalNav({
  items,
  active,
  onGo,
}: {
  items: NavItem[];
  /** The screen the URL names - where the last click or menu pick went. */
  active: TabKey;
  onGo: (tab: TabKey) => void;
}) {
  const t = useT();
  const current = useSectionInView(items, active);
  const rowRef = useRef<HTMLElement>(null);

  // On a phone the row is wider than the screen. Slide it so the marked chip
  // sits in the middle, without moving the page itself (no scrollIntoView).
  useEffect(() => {
    const row = rowRef.current;
    const chip = row?.querySelector<HTMLElement>("[aria-current=page]");
    if (!row || !chip || row.scrollWidth <= row.clientWidth) return;
    row.scrollTo({ left: chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2, behavior: "smooth" });
  }, [current]);

  return (
    <div className="sticky top-16 z-10 -mx-4 border-b border-border bg-background/90 backdrop-blur lg:top-24 lg:mx-0 lg:border-0 lg:bg-transparent lg:backdrop-blur-none">
      <nav
        ref={rowRef}
        aria-label={t("app.title")}
        className="flex gap-1 overflow-x-auto px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-lg:[mask-image:linear-gradient(to_right,transparent,black_16px,black_calc(100%-16px),transparent)] lg:flex-col lg:gap-0.5 lg:overflow-visible lg:p-0"
      >
        {items.map(({ key, icon }) => (
          <TabButton
            key={key}
            tab={key}
            Icon={icon}
            active={current === key}
            label={t(`tabs.${key}`)}
            onClick={() => onGo(key)}
          />
        ))}
      </nav>
    </div>
  );
}

/**
 * The screen whose section has reached the top of the window.
 *
 * "The top" is each section's own `scroll-margin-top`, the line a jump parks it
 * on, so a click and plain scrolling agree on what is in view. At the very
 * bottom of the page the last screen wins even if it is too short to reach that
 * line. While a jump is still scrolling, the mark stays on where it is going
 * instead of flickering through every screen it passes.
 */
function useSectionInView(items: NavItem[], active: TabKey): TabKey {
  const [current, setCurrent] = useState(active);
  const holdUntil = useRef(0);
  const keys = items.map((item) => item.key).join(",");

  useEffect(() => {
    setCurrent(active);
    holdUntil.current = performance.now() + JUMP_MS;
  }, [active]);

  useEffect(() => {
    const order = keys.split(",") as TabKey[];
    let frame = 0;
    const measure = () => {
      frame = 0;
      if (performance.now() < holdUntil.current) return;
      const sections = order.map((key) => document.getElementById(personalSectionId(key)));
      // A staff preview draws a notice instead of the sections: nothing to follow.
      if (sections.some((el) => !el)) return;
      const root = document.documentElement;
      if (window.innerHeight + window.scrollY >= root.scrollHeight - 2) {
        setCurrent(order[order.length - 1]!);
        return;
      }
      let next = order[0]!;
      sections.forEach((el, i) => {
        const line = parseFloat(getComputedStyle(el!).scrollMarginTop) + 8;
        if (el!.getBoundingClientRect().top <= line) next = order[i]!;
      });
      setCurrent(next);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    // A jump that ends early hands the mark back to the page at once.
    const settle = () => {
      holdUntil.current = 0;
      schedule();
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("scrollend", settle);
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("scrollend", settle);
      window.removeEventListener("resize", schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [keys]);

  return current;
}
